import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { CalendarDays, Download, Search, Trophy, Filter, Sparkles, AlertTriangle, Trash2, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { parseLocalDate } from "@/lib/date";

const todayISO = () => {
  // UK "today" — the backend uses UTC today_iso and BST/GMT is within
  // an hour of UTC either way, so aligning here keeps admin's date
  // ranges consistent with what the server considers "today".
  return new Date().toISOString().slice(0, 10);
};
const isoPlusDays = (d, n) => {
  // parseLocalDate returns noon-UTC on the target day, so operating
  // via UTC-day methods keeps arithmetic timezone-agnostic.
  const dt = parseLocalDate(d);
  dt.setUTCDate(dt.getUTCDate() + n);
  const y = dt.getUTCFullYear();
  const m = String(dt.getUTCMonth() + 1).padStart(2, "0");
  const day = String(dt.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

/**
 * Bulk-import fixtures from live-score-api into our matches collection.
 *
 * - Competition dropdown (backend gates which ids are valid)
 * - Date range with 5-day cap (backend enforces too)
 * - Posts to /live-score/import-fixtures
 * - Shows imported + skipped rows in a filterable table with a text search
 */
export default function LiveScoreImportCard() {
  const [competitions, setCompetitions] = useState([]);
  const [competition, setCompetition] = useState("international_tournament");
  const [fromDate, setFromDate] = useState(todayISO());
  const [toDate, setToDate] = useState(isoPlusDays(todayISO(), 2));
  const [importing, setImporting] = useState(false);
  const [lastImportSummary, setLastImportSummary] = useState(null);
  const [persistedMatches, setPersistedMatches] = useState([]);
  const [loadingPersisted, setLoadingPersisted] = useState(true);
  const [filter, setFilter] = useState("");

  // Track which match ids we just imported this session so the table can
  // tag them as "Just imported" (green dot) for the user's reference.
  const [recentlyImportedIds, setRecentlyImportedIds] = useState(new Set());

  const loadPersisted = async () => {
    setLoadingPersisted(true);
    try {
      const { data } = await api.get("/live-score/imported-matches");
      setPersistedMatches(data?.matches || []);
    } catch {
      /* non-fatal — keep showing whatever we already had */
    } finally {
      setLoadingPersisted(false);
    }
  };

  useEffect(() => {
    api
      .get("/live-score/competitions")
      .then(({ data }) => {
        setCompetitions(data || []);
        if (data?.length && !data.some((c) => c.key === competition)) {
          setCompetition(data[0].key);
        }
      })
      .catch(() => {
        setCompetitions([
          { key: "international_tournament", label: "International tournament", id: 362 },
          { key: "premier_league", label: "Premier League", id: 2 },
          { key: "champions_league", label: "Champions League", id: 268 },
          { key: "europa_league", label: "Europa League", id: 245 },
          { key: "english_championship", label: "English Championship", id: 77 },
          { key: "fa_cup", label: "FA Cup", id: 152 },
          { key: "league_cup", label: "EFL Cup", id: 150 },
        ]);
      });
    loadPersisted();
  }, []);

  const runImport = async (range) => {
    const f = range?.from ?? fromDate;
    const t = range?.to ?? toDate;
    if (!f || !t) {
      toast.error("Pick a start and end date");
      return;
    }
    setImporting(true);
    setLastImportSummary(null);
    try {
      const { data } = await api.post("/live-score/import-fixtures", {
        from: f,
        to: t,
        competition,
      });
      setLastImportSummary({
        imported_count: data?.imported_count ?? 0,
        skipped_count: data?.skipped_count ?? 0,
        errors: data?.errors || [],
      });
      const justImported = new Set((data?.imported || []).map((m) => m.id).filter(Boolean));
      setRecentlyImportedIds(justImported);
      const i = data?.imported_count ?? 0;
      const s = data?.skipped_count ?? 0;
      if (i > 0) {
        toast.success(`Imported ${i} fixture${i === 1 ? "" : "s"}${s ? ` · skipped ${s} (already in DB)` : ""}`);
      } else if (s > 0) {
        toast.message(`No new fixtures — ${s} already in your DB`);
      } else {
        toast.message("No fixtures returned for that range");
      }
      await loadPersisted();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setImporting(false);
    }
  };

  const importNext5Days = async () => {
    const from = todayISO();
    const to = isoPlusDays(from, 4);
    setFromDate(from);
    setToDate(to);
    await runImport({ from, to });
  };

  // ---- Inline row actions on the persisted matches table ----
  const [busyRowId, setBusyRowId] = useState(null);

  const applyRowEdit = (rowId, patch) => {
    setPersistedMatches((list) =>
      (list || []).map((m) => (m.id === rowId ? { ...m, ...patch } : m)),
    );
  };

  const removeRow = (rowId) => {
    setPersistedMatches((list) => (list || []).filter((m) => m.id !== rowId));
    setRecentlyImportedIds((s) => {
      if (!s.has(rowId)) return s;
      const next = new Set(s);
      next.delete(rowId);
      return next;
    });
  };

  const editRowDate = async (m, newDate) => {
    if (!m.id || !newDate || newDate === m.match_date) return;
    setBusyRowId(m.id);
    try {
      await api.patch(`/matches/${m.id}`, { match_date: newDate });
      applyRowEdit(m.id, { match_date: newDate });
      toast.success(`${m.home_team} v ${m.away_team} moved to ${newDate}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusyRowId(null);
    }
  };

  const editRowKickoff = async (m, newKickoff) => {
    const k = (newKickoff || "").trim();
    if (!m.id || !k || k === m.kickoff) return;
    setBusyRowId(m.id);
    try {
      await api.patch(`/matches/${m.id}`, { kickoff: k });
      applyRowEdit(m.id, { kickoff: k });
      toast.success(`${m.home_team} v ${m.away_team} kickoff set to ${k}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusyRowId(null);
    }
  };

  const deleteRow = async (m) => {
    if (!m.id) return;
    const ok = window.confirm(
      `Delete ${m.home_team} v ${m.away_team} on ${m.match_date}? Any predictions on it will also be removed.`,
    );
    if (!ok) return;
    setBusyRowId(m.id);
    try {
      // Hard delete — matches the confirmation copy ("Any predictions on
      // it will also be removed") and prevents the archived row from
      // blocking a subsequent re-import via the dedupe check.
      await api.delete(`/matches/${m.id}?hard=true`);
      removeRow(m.id);
      toast.success(`${m.home_team} v ${m.away_team} deleted`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusyRowId(null);
    }
  };

  const filteredRows = useMemo(() => {
    if (!filter.trim()) return persistedMatches;
    const q = filter.toLowerCase();
    return (persistedMatches || []).filter(
      (r) =>
        (r.home_team || "").toLowerCase().includes(q) ||
        (r.away_team || "").toLowerCase().includes(q) ||
        (r.match_date || "").includes(q),
    );
  }, [persistedMatches, filter]);

  return (
    <section
      data-testid="live-score-import-card"
      className="border border-zinc-200 rounded-sm bg-white p-6"
    >
      <header className="mb-5">
        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 inline-flex items-center gap-1.5">
          <Download className="h-3.5 w-3.5" /> Bulk import
        </p>
        <h2 className="font-heading text-2xl font-bold tracking-tight text-zinc-950 mt-1">Import fixtures from live-score-api.</h2>
        <p className="text-sm text-zinc-500 mt-2 max-w-2xl">
          Pull every {competitions.find((c) => c.key === competition)?.label || "selected"} fixture for a date
          range straight into your matches list. Fixtures only appear on the Predictions page on their actual
          match-day, so importing the next few days ahead is safe.
        </p>
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
        <div>
          <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500 inline-flex items-center gap-1.5">
            <Trophy className="h-3 w-3" /> Competition
          </Label>
          <Select value={competition} onValueChange={setCompetition}>
            <SelectTrigger
              data-testid="import-competition-select"
              className="mt-1 h-11 rounded-sm border-zinc-300"
            >
              <SelectValue placeholder="Pick a competition" />
            </SelectTrigger>
            <SelectContent>
              {competitions.map((c) => (
                <SelectItem
                  key={c.key}
                  value={c.key}
                  data-testid={`import-competition-${c.key}`}
                >
                  {c.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500 inline-flex items-center gap-1.5">
            <CalendarDays className="h-3 w-3" /> Start date
          </Label>
          <Input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            data-testid="import-from-date"
            className="mt-1 h-11 rounded-sm border-zinc-300"
          />
        </div>
        <div>
          <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500 inline-flex items-center gap-1.5">
            <CalendarDays className="h-3 w-3" /> End date
          </Label>
          <Input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            data-testid="import-to-date"
            className="mt-1 h-11 rounded-sm border-zinc-300"
          />
        </div>
        <Button
          type="button"
          onClick={() => runImport()}
          disabled={importing}
          data-testid="import-submit"
          className="h-11 px-5 rounded-sm bg-zinc-950 hover:bg-zinc-800 text-white font-semibold tracking-tight active:scale-[0.98]"
        >
          {importing ? (
            <span className="inline-flex items-center gap-2">
              <Sparkles className="h-4 w-4 animate-spin" /> Importing…
            </span>
          ) : (
            <span className="inline-flex items-center gap-2">
              <Download className="h-4 w-4" /> Import
            </span>
          )}
        </Button>
      </div>

      <div className="mt-3 flex items-center justify-between flex-wrap gap-2">
        <p className="text-[11px] text-zinc-500">
          Max 5 days per import. Existing fixtures (same teams + date) are skipped automatically.
        </p>
        <Button
          type="button"
          onClick={importNext5Days}
          disabled={importing}
          variant="outline"
          data-testid="import-next-5-days"
          className="h-8 px-3 rounded-sm border-zinc-300 text-zinc-700 hover:bg-zinc-50 text-[11px] font-semibold tracking-tight"
        >
          <CalendarDays className="h-3 w-3 mr-1.5" /> Import next 5 days
        </Button>
      </div>

      <div className="mt-6" data-testid="import-results">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
          <div className="flex items-center gap-3 flex-wrap">
            <span
              data-testid="import-current-count"
              className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full border border-zinc-200 bg-white text-zinc-800 text-[11px] font-semibold"
              title="Imported fixtures kept here until the day after they play"
            >
              <Download className="h-3 w-3" /> {persistedMatches.length} active imported fixture{persistedMatches.length === 1 ? "" : "s"}
            </span>
            {lastImportSummary && (
              <>
                <span
                  data-testid="import-result-imported"
                  className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-800 text-[11px] font-semibold"
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> {lastImportSummary.imported_count} new this run
                </span>
                <span
                  data-testid="import-result-skipped"
                  className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full border border-zinc-200 bg-zinc-50 text-zinc-700 text-[11px] font-semibold"
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-zinc-400" /> {lastImportSummary.skipped_count} already in DB
                </span>
                {(lastImportSummary.errors || []).length > 0 && (
                  <span
                    data-testid="import-result-errors"
                    className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full border border-amber-300 bg-amber-50 text-amber-900 text-[11px] font-semibold"
                    title={(lastImportSummary.errors || []).map((e) => `${e.date}: ${e.error}`).join("\n")}
                  >
                    <AlertTriangle className="h-3 w-3" /> {lastImportSummary.errors.length} api error{lastImportSummary.errors.length === 1 ? "" : "s"}
                  </span>
                )}
              </>
            )}
            {loadingPersisted && (
              <span className="inline-flex items-center gap-1.5 text-[11px] text-zinc-500">
                <Loader2 className="h-3 w-3 animate-spin" /> Loading…
              </span>
            )}
          </div>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-400 pointer-events-none" />
            <Input
              type="text"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter by team or date…"
              data-testid="import-filter-input"
              className="h-9 w-64 pl-8 rounded-sm border-zinc-300 text-sm"
            />
          </div>
        </div>

        {persistedMatches.length === 0 ? (
          <p
            data-testid="import-empty"
            className="text-sm text-zinc-500 py-6 text-center border border-dashed border-zinc-300 rounded-sm"
          >
            No active imported fixtures yet — pick a date range above and click <strong>Import</strong>.
          </p>
        ) : (
          <div className="border border-zinc-200 rounded-sm overflow-hidden">
            <table className="w-full text-sm" data-testid="import-table">
              <thead className="bg-zinc-50">
                <tr className="text-left text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">
                  <th className="px-3 py-2 border-b border-zinc-200">Date</th>
                  <th className="px-3 py-2 border-b border-zinc-200">Kickoff</th>
                  <th className="px-3 py-2 border-b border-zinc-200">Home</th>
                  <th className="px-3 py-2 border-b border-zinc-200">Away</th>
                  <th className="px-3 py-2 border-b border-zinc-200 inline-flex items-center gap-1">
                    <Filter className="h-3 w-3" /> Status
                  </th>
                  <th className="px-3 py-2 border-b border-zinc-200 text-right">Actions</th>
                </tr>
              </thead>
              <tbody data-testid="import-table-body">
                {filteredRows.map((r, i) => {
                  const isBusy = busyRowId === r.id;
                  const isJustImported = recentlyImportedIds.has(r.id);
                  const isFinished = r.status === "finished";
                  return (
                    <tr
                      key={`${r.id}-${i}`}
                      data-testid={`import-row-${i}`}
                      className="hover:bg-zinc-50/60"
                    >
                      <td className="px-3 py-2 border-b border-zinc-100">
                        <Input
                          type="date"
                          defaultValue={r.match_date}
                          onChange={(e) => editRowDate(r, e.target.value)}
                          disabled={isBusy}
                          data-testid={`import-row-date-${r.id}`}
                          className="h-8 w-36 rounded-sm border-zinc-300 text-xs"
                        />
                      </td>
                      <td className="px-3 py-2 border-b border-zinc-100">
                        <Input
                          type="time"
                          defaultValue={r.kickoff || ""}
                          onBlur={(e) => editRowKickoff(r, e.target.value)}
                          disabled={isBusy}
                          data-testid={`import-row-kickoff-${r.id}`}
                          className="h-8 w-24 rounded-sm border-zinc-300 text-xs font-mono tabular-nums"
                        />
                      </td>
                      <td className="px-3 py-2 border-b border-zinc-100 font-semibold">{r.home_team}</td>
                      <td className="px-3 py-2 border-b border-zinc-100 font-semibold">{r.away_team}</td>
                      <td className="px-3 py-2 border-b border-zinc-100">
                        {isFinished ? (
                          <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-zinc-700">
                            <span className="h-1.5 w-1.5 rounded-full bg-zinc-700" /> Finished
                          </span>
                        ) : isJustImported ? (
                          <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-emerald-700">
                            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Just imported
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-zinc-500">
                            <span className="h-1.5 w-1.5 rounded-full bg-zinc-400" /> In DB
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 border-b border-zinc-100 text-right whitespace-nowrap">
                        {isBusy && <Loader2 className="h-3.5 w-3.5 animate-spin text-zinc-500 inline-block mr-2" />}
                        <Button
                          type="button"
                          onClick={() => deleteRow(r)}
                          disabled={isBusy}
                          variant="outline"
                          data-testid={`import-row-delete-${r.id}`}
                          title="Delete this fixture (and any predictions on it)"
                          className="h-7 px-2 rounded-sm border-rose-200 text-rose-700 hover:bg-rose-50 hover:text-rose-800"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
                {filteredRows.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-3 py-5 text-center text-xs text-zinc-500">
                      No matches for filter &quot;{filter}&quot;.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}
